// frontend/src/services/websocket.js - Complete updated WebSocket service
class WebSocketService {
  constructor() {
    this.ws = null
    this.reconnectInterval = 5000
    this.maxReconnectAttempts = 5
    this.reconnectAttempts = 0
    this.listeners = new Map()
    this.currentSessionId = null
  }

  connect(sessionId) {
    this.currentSessionId = sessionId
    
    // Fix WebSocket URL construction - hardcode for now to eliminate variable issues
    const wsUrl = `wss://api.testcrew.ai/ws/${sessionId}`
    
    console.log('🔗 Attempting WebSocket connection to:', wsUrl)
    
    try {
      this.ws = new WebSocket(wsUrl)
      
      this.ws.onopen = () => {
        console.log('✅ WebSocket connected successfully')
        this.reconnectAttempts = 0
        this.notifyListeners('open', { sessionId })
      }

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          console.log('📨 WebSocket message received:', message.type, message)
          
          // Special logging for video frames
          if (message.type === 'video_frame') {
            console.log('🎥 VIDEO FRAME RECEIVED:', {
              timestamp: message.data.timestamp,
              frameSize: message.data.frame ? message.data.frame.length : 'no frame data'
            })
          }
          
          this.notifyListeners('message', message)
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error, event.data)
        }
      }

      this.ws.onclose = (event) => {
        console.log('❌ WebSocket disconnected:', event.code, event.reason)
        this.notifyListeners('close', { code: event.code, reason: event.reason })
        
        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
          console.log(`🔄 Attempting to reconnect... (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`)
          setTimeout(() => {
            this.reconnectAttempts++
            this.connect(sessionId)
          }, this.reconnectInterval)
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('💀 Max reconnection attempts reached')
        }
      }

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error)
        console.error('🔍 Failed URL was:', wsUrl)
        this.notifyListeners('error', error)
      }

    } catch (error) {
      console.error('💥 Failed to create WebSocket connection:', error)
      console.error('🔍 Attempted URL:', wsUrl)
    }
  }

  disconnect(sendCleanup = false) {
    if (sendCleanup && this.currentSessionId && this.isConnected()) {
      // Send cleanup message before disconnecting
      this.send({
        type: 'cleanup_session',
        data: {
          session_id: this.currentSessionId,
          reason: 'manual_disconnect',
          timestamp: new Date().toISOString()
        }
      })
      
      // Wait a moment for message to send
      setTimeout(() => {
        if (this.ws) {
          this.ws.close(1000, 'Manual disconnect with cleanup')
          this.ws = null
        }
      }, 100)
    } else {
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    }
    
    this.currentSessionId = null
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('📤 Sending WebSocket message:', message.type)
      this.ws.send(JSON.stringify(message))
    } else {
      console.warn('⚠️ WebSocket is not connected, message not sent:', message.type)
      console.warn('🔍 WebSocket state:', this.ws ? this.ws.readyState : 'null')
    }
  }

  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event).push(callback)
  }

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event)
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in WebSocket listener:', error)
        }
      })
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }

  getEndpointUrl() {
    return `wss://api.testcrew.ai`
  }

  getCurrentSessionId() {
    return this.currentSessionId
  }
}

export default new WebSocketService()